import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import { useActiveGroup } from '../hooks/useActiveGroup';
import { useAuth } from '../hooks/useAuth';
import { 
  Users, 
  DollarSign, 
  FolderRoot as Football, 
  TrendingUp, 
  Trophy, 
  Calendar, 
  Wallet, 
  Plus, 
  Clock, 
  MapPin, 
  ArrowUpCircle, 
  ArrowDownCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { AreaChart, Area } from 'recharts';
import { format, startOfMonth, endOfMonth, parseISO, subMonths, startOfYear, endOfYear } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import bannerApp from '../images/banner_app.jpg';
import bannerAppMobile from '../images/banner_app_mobile.gif';

interface DashboardStats {
  totalGrupos: number;
  totalJogadores: number;
  saldoAtual: number;
}

interface PlayerParticipation {
  id: string;
  nome: string;
  participacoes: number;
}

interface ChartData {
  data: string;
  participacoes: number;
}

interface FinancialMovement {
  mes: string;
  receitas: number;
  despesas: number;
  saldo: number;
}

const DashboardPage: React.FC = () => {
  const { supabase, testConnection } = useSupabase();
  const { activeGroup } = useActiveGroup();
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    totalGrupos: 0,
    totalJogadores: 0,
    saldoAtual: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grupoAtivo, setGrupoAtivo] = useState<any>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const [topPlayers, setTopPlayers] = useState<PlayerParticipation[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'month' | 'year'>('month');
  const [monthlyBalance, setMonthlyBalance] = useState<number>(0);
  const [cashBalance, setCashBalance] = useState<number>(0);
  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [financialData, setFinancialData] = useState<FinancialMovement[]>([]);
  const [selectedChartPeriod, setSelectedChartPeriod] = useState<'month' | 'year'>('month');

  const handleRetry = () => {
    setError(null);
    setLoading(true);
    fetchData();
  };

  const fetchData = async () => {
    if (!user) return;

    try {
      setError(null);
      
      // Test connection first
      console.log('Testing Supabase connection...');
      const connectionOk = await testConnection();
      if (!connectionOk) {
        throw new Error('Não foi possível conectar ao banco de dados. Verifique sua conexão com a internet.');
      }

      // First, get active group from profile
      console.log('Fetching user profile...');
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('grupo_ativo')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Profile fetch error:', profileError);
        throw new Error(`Erro ao buscar perfil: ${profileError.message}`);
      }

      if (profileData?.grupo_ativo) {
        // Get current month's transactions
        const startDate = startOfMonth(new Date()).toISOString().split('T')[0];
        const endDate = endOfMonth(new Date()).toISOString().split('T')[0];

        console.log('Fetching financial data...');
        const { data: financasData, error: financasError } = await supabase
          .from('financas')
          .select('tipo, valor')
          .eq('grupo_id', profileData.grupo_ativo)
          .gte('data', startDate)
          .lte('data', endDate);

        if (financasError) {
          console.warn('Financial data fetch error:', financasError);
        }

        // Calculate current month's balance
        let monthBalance = 0;
        if (financasData) {
          monthBalance = financasData.reduce((acc, item) => {
            return item.tipo === 'receita' 
              ? acc + item.valor 
              : acc - item.valor;
          }, 0);
        }

        setMonthlyBalance(monthBalance);

        // Get cash balance from group settings
        console.log('Fetching group configuration...');
        const { data: configData, error: configError } = await supabase
          .from('grupo_configuracoes')
          .select('caixa_atual')
          .eq('grupo_id', profileData.grupo_ativo)
          .single();

        if (configError) {
          console.warn('Config fetch error:', configError);
        }

        const currentCashBalance = configData?.caixa_atual || 0;
        setCashBalance(currentCashBalance);
        
        // Calculate total balance
        setTotalBalance(currentCashBalance + monthBalance);

        // Fetch financial movements data
        await fetchFinancialData(profileData.grupo_ativo, selectedChartPeriod);

        setSelectedPeriod('month');
        await fetchParticipationData(profileData.grupo_ativo, 'month');
        await fetchTopPlayers(profileData.grupo_ativo);

        console.log('Fetching group data...');
        const { data: grupoData, error: grupoError } = await supabase
          .from('grupos')
          .select('*')
          .eq('id', profileData.grupo_ativo)
          .single();

        if (grupoError) {
          console.warn('Group fetch error:', grupoError);
        }

        setGrupoAtivo(grupoData);

        console.log('Fetching player count...');
        const { count: jogadoresCount, error: playersError } = await supabase
          .from('jogadores')
          .select('*', { count: 'exact', head: true })
          .eq('grupo_id', profileData.grupo_ativo)
          .eq('ativo', true);

        if (playersError) {
          console.warn('Players count error:', playersError);
        }

        console.log('Fetching groups count...');
        const { count: totalGrupos, error: groupsError } = await supabase
          .from('grupos')
          .select('id', { count: 'exact', head: true })
          .eq('criado_por', user.id);

        if (groupsError) {
          console.warn('Groups count error:', groupsError);
        }

        setStats({
          totalGrupos: totalGrupos || 0,
          totalJogadores: jogadoresCount || 0,
          saldoAtual: totalBalance
        });
      }
    } catch (error: any) {
      console.error('Erro ao buscar dados do dashboard:', error);
      
      let errorMessage = 'Erro ao carregar dados do dashboard';
      if (error.message?.includes('Failed to fetch')) {
        errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
      } else if (error.message?.includes('CORS')) {
        errorMessage = 'Erro de configuração. Entre em contato com o suporte.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [supabase, user, selectedChartPeriod]);

  const fetchParticipationData = async (groupId: string, period: 'day' | 'month' | 'year') => {
    try {
      let startDate: Date;
      let endDate: Date;
      
      if (period === 'day') {
        // Last 30 days
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        endDate = new Date();
      } else if (period === 'month') {
        // Last 6 months
        startDate = subMonths(startOfMonth(new Date()), 5);
        endDate = endOfMonth(new Date());
      } else {
        // Current year
        startDate = startOfYear(new Date());
        endDate = endOfYear(new Date());
      }

      const { data: jogosData, error } = await supabase
        .from('jogos')
        .select(`
          id,
          data,
          jogos_jogadores(jogador_id)
        `)
        .eq('grupo_id', groupId)
        .gte('data', startDate.toISOString())
        .lte('data', endDate.toISOString())
        .order('data');

      if (error) {
        console.warn('Participation data fetch error:', error);
        return;
      }

      if (jogosData) {
        const formattedData = jogosData.map(jogo => ({
          data: period === 'day' 
            ? format(parseISO(jogo.data), 'dd/MM', { locale: ptBR })
            : period === 'month'
            ? format(parseISO(jogo.data), 'dd/MM', { locale: ptBR })
            : format(parseISO(jogo.data), 'MMM', { locale: ptBR }),
          participacoes: jogo.jogos_jogadores?.length || 0,
          jogo: format(parseISO(jogo.data), 'dd/MM/yyyy', { locale: ptBR })
        }));

        setChartData(formattedData);
      }
    } catch (error) {
      console.error('Erro ao buscar dados de participação:', error);
    }
  };

  const fetchTopPlayers = async (groupId: string) => {
    try {
      const { data: participations, error } = await supabase
        .from('jogos_jogadores')
        .select(`
          jogador:jogadores(id, nome),
          jogo:jogos(data)
        `)
        .eq('jogos.grupo_id', groupId)
        .order('jogo(data)', { ascending: false });

      if (error) {
        console.warn('Top players fetch error:', error);
        return;
      }

      if (participations) {
        const playerStats = participations.reduce((acc: Record<string, PlayerParticipation>, curr) => {
          const jogador = Array.isArray(curr.jogador) ? curr.jogador[0] : curr.jogador;
          if (!jogador) return acc;
          const playerId = jogador.id;
          if (!acc[playerId]) {
            acc[playerId] = {
              id: playerId,
              nome: jogador.nome,
              participacoes: 0
            };
          }
          acc[playerId].participacoes++;
          return acc;
        }, {});

        const topThree = Object.values(playerStats)
          .sort((a, b) => b.participacoes - a.participacoes)
          .slice(0, 3);

        setTopPlayers(topThree);
      }
    } catch (error) {
      console.error('Erro ao buscar top jogadores:', error);
    }
  };

  const fetchFinancialData = async (groupId: string, period: 'month' | 'year') => {
    try {
      let startDate: Date;
      let endDate: Date;
      let formatPattern: string;
      let months: number;

      if (period === 'month') {
        startDate = subMonths(startOfMonth(new Date()), 5); // Last 6 months
        endDate = endOfMonth(new Date());
        formatPattern = 'MMM';
        months = 6;
      } else {
        startDate = startOfYear(new Date());
        endDate = endOfYear(new Date());
        formatPattern = 'MMM';
        months = 12;
      }

      const { data: movements, error } = await supabase
        .from('financas')
        .select('tipo, valor, data')
        .eq('grupo_id', groupId)
        .gte('data', startDate.toISOString())
        .lte('data', endDate.toISOString())
        .order('data');

      if (error) {
        console.warn('Financial movements fetch error:', error);
        return;
      }

      if (movements) {
        const monthlyData: { [key: string]: FinancialMovement } = {};

        // Initialize all months
        for (let i = 0; i < months; i++) {
          const date = period === 'month' 
            ? subMonths(new Date(), months - 1 - i)
            : new Date(new Date().getFullYear(), i, 1);
          
          const monthKey = format(date, 'yyyy-MM');
          monthlyData[monthKey] = {
            mes: format(date, formatPattern, { locale: ptBR }),
            receitas: 0,
            despesas: 0,
            saldo: 0
          };
        }

        // Aggregate movements
        movements.forEach(movement => {
          const monthKey = format(new Date(movement.data), 'yyyy-MM');
          if (monthlyData[monthKey]) {
            if (movement.tipo === 'receita') {
              monthlyData[monthKey].receitas += movement.valor;
            } else {
              monthlyData[monthKey].despesas += movement.valor;
            }
            monthlyData[monthKey].saldo = 
              monthlyData[monthKey].receitas - monthlyData[monthKey].despesas;
          }
        });

        setFinancialData(Object.values(monthlyData));
      }
    } catch (error) {
      console.error('Erro ao buscar dados financeiros:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2 text-red-600 dark:text-red-400">Erro ao carregar dashboard</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4 max-w-md mx-auto">
          {error}
        </p>
        <button 
          onClick={handleRetry}
          className="btn-primary inline-flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!grupoAtivo) {
    return (
      <div className="text-center py-12">
        <Football className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Nenhum grupo selecionado</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          Selecione um grupo ativo para visualizar o dashboard
        </p>
        <Link to="/grupos" className="btn-primary">
          Ir para Grupos
        </Link>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Bem-vindo de volta ao Presida.Club, seu gerenciador de times de futebol amador.
        </p>
      </header>

      {/* Banner */}
      <div className="mb-8 overflow-hidden rounded-lg">
        <picture>
  <source
    media="(min-width: 640px)"
    srcSet={bannerApp}
  />
  <img
    src={bannerAppMobile}
    alt="Presida.Club Banner"
    className="w-full h-auto object-cover rounded-[20px]"
  />
</picture>


      </div>

    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
  {[
    {
      label: 'Saldo do Mês',
      value: monthlyBalance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      icon: <DollarSign className="w-6 h-6 text-primary-500" />,
      footer: 'Ver financeiro →',
      link: '/financeiro',
      color: monthlyBalance >= 0 ? 'green' : 'red',
      subtitle: format(new Date(), 'MMMM yyyy', { locale: ptBR })
    },
    {
      label: 'Grupos',
      value: stats.totalGrupos,
      icon: <Football className="w-6 h-6 text-primary-500" />,
      footer: 'Ver detalhes →',
      link: '/grupos',
      color: 'primary'
    },
    {
      label: 'Jogadores',
      value: stats.totalJogadores,
      icon: <Users className="w-6 h-6 text-blue-500" />,
      footer: 'Gerenciar jogadores →',
      link: '/jogadores',
      color: 'blue'
    },
    {
      label: 'Dinheiro em Caixa',
      value: totalBalance.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      icon: <Wallet className="w-6 h-6 text-green-500" />,
      footer: 'Ver financeiro →',
      link: '/financeiro',
      color: totalBalance >= 0 ? 'green' : 'red'
    }
  ].map((card, index) => (
    <Link
      key={index}
      to={card.link}
      className="card p-5 text-left hover:shadow-md transition-shadow"
    >
      <div className={`p-3 rounded-full mb-4 w-fit ${
        card.color === 'green' ? 'bg-green-100 dark:bg-green-900/20' :
        card.color === 'red' ? 'bg-red-100 dark:bg-red-900/20' :
        card.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900/20' :
        'bg-primary-100 dark:bg-primary-900/20'
      }`}>
        {card.icon}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
      <h3 className={`text-xl font-bold mt-1 ${
        card.color === 'green' ? 'text-green-600 dark:text-green-400' :
        card.color === 'red' ? 'text-red-600 dark:text-red-400' :
        card.color === 'blue' ? 'text-blue-600 dark:text-blue-400' :
        'text-primary-600 dark:text-primary-400'
      }`}>
        {card.value}
      </h3>
      {card.subtitle && (
        <p className="text-sm text-gray-500 mt-1">{card.subtitle}</p>
      )}
      <span className="mt-3 block text-sm text-primary-600 dark:text-primary-400 hover:underline">
        {card.footer}
      </span>
    </Link>
  ))}
</div>


    <div className="card p-6 mb-8">
  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
    <h2 className="text-lg font-bold">Movimentações Financeiras</h2>
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => {
          setSelectedChartPeriod('month');
          fetchFinancialData(grupoAtivo.id, 'month');
        }}
        className={`btn ${
          selectedChartPeriod === 'month' ? 'btn-primary' : 'btn-outline'
        }`}
      >
        Últimos 6 Meses
      </button>
      <button
        onClick={() => {
          setSelectedChartPeriod('year');
          fetchFinancialData(grupoAtivo.id, 'year');
        }}
        className={`btn ${
          selectedChartPeriod === 'year' ? 'btn-primary' : 'btn-outline'
        }`}
      >
        Ano Atual
      </button>
    </div>
  </div>

  <div className="h-[300px]">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={financialData}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="mes" />
        <YAxis />
        <Tooltip 
          contentStyle={{
            backgroundColor: '#1f2937', // bg-gray-800
            border: '1px solid #374151', // border-gray-700
            borderRadius: '8px',
            color: '#f9fafb', // text-gray-50
          }}
          labelStyle={{ color: '#f9fafb' }}
          formatter={(value: number) => 
            value.toLocaleString('pt-BR', { 
              style: 'currency', 
              currency: 'BRL' 
            })
          }
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="receitas" 
          name="Receitas"
          stroke="#00FF7F" 
          strokeWidth={2}
          dot={{ r: 4 }}
        />
        <Line 
          type="monotone" 
          dataKey="despesas" 
          name="Despesas"
          stroke="#FF4444" 
          strokeWidth={2}
          dot={{ r: 4 }}
        />
        <Line 
          type="monotone" 
          dataKey="saldo" 
          name="Saldo"
          stroke="#4A90E2" 
          strokeWidth={2}
          dot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-100 dark:bg-green-900/20">
      <ArrowUpCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Total Receitas</p>
        <p className="font-bold text-green-600 dark:text-green-400">
          {financialData.reduce((acc, item) => acc + item.receitas, 0)
            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
        </p>
      </div>
    </div>

    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-100 dark:bg-red-900/20">
      <ArrowDownCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Total Despesas</p>
        <p className="font-bold text-red-600 dark:text-red-400">
          {financialData.reduce((acc, item) => acc + item.despesas, 0)
            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
        </p>
      </div>
    </div>

    <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-100 dark:bg-blue-900/20">
      <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">Saldo Período</p>
        <p className={`font-bold ${
          financialData.reduce((acc, item) => acc + item.saldo, 0) >= 0
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        }`}>
          {financialData.reduce((acc, item) => acc + item.saldo, 0)
            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
        </p>
      </div>
    </div>
  </div>
</div>



<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
  <div className="lg:col-span-2">
    <div className="card p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 gap-4">
        <h2 className="text-lg font-bold">Participações em Jogos</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setSelectedPeriod('day');
              fetchParticipationData(grupoAtivo.id, 'day');
            }}
            className={`btn ${
              selectedPeriod === 'day' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            Dia
          </button>
          <button
            onClick={() => {
              setSelectedPeriod('month');
              fetchParticipationData(grupoAtivo.id, 'month');
            }}
            className={`btn ${
              selectedPeriod === 'month' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            Mês
          </button>
          <button
            onClick={() => {
              setSelectedPeriod('year');
              fetchParticipationData(grupoAtivo.id, 'year');
            }}
            className={`btn ${
              selectedPeriod === 'year' ? 'btn-primary' : 'btn-outline'
            }`}
          >
            Ano
          </button>
        </div>
      </div>

            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="data" />
                  <YAxis />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                      color: '#f9fafb'
                    }}
                    labelStyle={{ color: '#f9fafb' }}
                    formatter={(value: number, name: string) => [
                      `${value} participantes`,
                      'Participações'
                    ]}
                    labelFormatter={(label: string) => `Data: ${label}`}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="participacoes" 
                    stroke="#00FF7F" 
                    fill="#00FF7F"
                    fillOpacity={0.3}
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-bold mb-6">Top Jogadores</h2>
          <div className="space-y-4">
            {topPlayers.map((player, index) => (
              <div key={player.id} className="flex items-center gap-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  index === 0 ? 'bg-yellow-100 text-yellow-600' :
                  index === 1 ? 'bg-gray-100 text-gray-600' :
                  'bg-orange-100 text-orange-600'
                }`}>
                  <Trophy className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{player.nome}</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <p className="text-sm text-gray-500">
                      {player.participacoes} participações
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card p-6">
        <h2 className="text-xl font-bold mb-4">Ações rápidas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/jogadores" className="btn-outline text-center">
            Adicionar jogador
          </Link>
          <Link to="/financeiro" className="btn-outline text-center">
            Registrar pagamento
          </Link>
          <Link to="/sorteio" className="btn-outline text-center">
            Sortear times
          </Link>
          <Link to="/resenha" className="btn-outline text-center">
            Nova resenha
          </Link>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;